import { KTIcon } from "../../../_metronic/helpers";
import { usePeople } from "../../../coreGlobal/providers/PersonProvider";
import { PersonInfo } from "../../../components/Person/PersonInfo";
import SearchPersonForm from "../../../components/Person/SearchPersonForm";
import { PersonCreateModal } from "../../../components/Person/PersonCreateModal";
import { IPersonResponse, TPersonRequest } from "../../../coreGlobal/models/person";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { createOwner, findPersonByDocument, updateOwners, validateOwnerPerson } from "../core/_request";
import { IExistingPerson } from "../../../components/Person/ProfileForm";
import { useFormFranchise } from "../core/franchiseProvider";

const emptyField = () => ({ name: '', document: '', dropdownOpen: false, isFetching: false, search: [] })

export const OwnerForm = ({ formik }) => {
  const people = usePeople()
  const { persons, setPersons, fieldPersons, setFieldPersons, showCreatePersonModal, setShowCreatePersonModal, removePerson } = people
  const { franchise } = useFormFranchise()
  const [loading, setLoading] = useState(false)
  const [savedOwners, setSavedOwners] = useState<IPersonResponse[]>([])

  useEffect(() => {
    setSavedOwners((franchise?.owners as unknown as IPersonResponse[]) ?? [])
  }, [franchise])

  const currentOwners = persons.filter(Boolean)
  const currentOwnerIds = currentOwners.map((person) => person.id).sort()
  const savedOwnerIds = savedOwners.map((person) => person.id).sort()
  const isDirty = currentOwnerIds.join(',') !== savedOwnerIds.join(',')

  const addField = () => {
    setFieldPersons([...fieldPersons, emptyField()]);
  };

  // Esvazia a posição em vez de removê-la, para manter persons alinhado com fieldPersons
  const clearPerson = (index: number) => {
    const updatedPersons = [...persons]
    updatedPersons[index] = undefined as unknown as IPersonResponse
    setPersons(updatedPersons)
  }

  // O flag de edição do provider é compartilhado; controla por índice para abrir só o modal da linha clicada
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const ownerProvider = (index: number) => () => ({
    ...people,
    removePerson: clearPerson,
    showEditPersonModal: editingIndex === index,
    setShowEditPersonModal: (show: boolean) => setEditingIndex(show ? index : null),
  })

  const discardChanges = () => {
    setPersons(savedOwners)
    setFieldPersons(savedOwners.length ? savedOwners.map(emptyField) : [emptyField()])
  }

  const removeField = (key: number) => {
    
    if (fieldPersons.length > 1) {
      const newArray = [...fieldPersons]
      newArray.splice(key, 1)
      setFieldPersons(newArray);
      removePerson(key)
    }
  };

  const initialValues: TPersonRequest = {
    type: 1,
    name: '',
    preferredName: '',
    email: '',
    document: '',
    birthDate: '',
    gender: '',
    rg: '',
    job: '',
    nationality: 22,
    addresses: [],
    bankData: [],
    medicalConditions:[],
    reduceTime: true,
    hideAvatar: false
  }

  const emptyFieldIndex = fieldPersons.findIndex((_, index) => persons?.[index] === undefined)
  const createPersonIndex = emptyFieldIndex === -1 ? fieldPersons.length : emptyFieldIndex

  // Documento já cadastrado: adiciona a pessoa como sócia e abre a edição com os dados dela
  const addExistingOwner = async (existing: IExistingPerson, document: string) => {
    const result = await Swal.fire({
      icon: 'info',
      title: 'Pessoa já cadastrada',
      html: `
        <p>Deseja adicioná-la como sócia?</p>
        <div class="text-start">
          <div class="mb-2"><b>Nome:</b> ${existing.name}</div>
          <div class="mb-2"><b>E-mail:</b> ${existing.email ?? '-'}</div>
          <div class="mb-2"><b>CPF:</b> ${document}</div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Adicionar como sócio',
      cancelButtonText: 'Tentar outro',
      allowOutsideClick: false,
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary'
      }
    })
    if (!result.isConfirmed) return

    if (currentOwners.some((person) => person.id === existing.id)) {
      await Swal.fire({ icon: 'warning', title: 'Esta pessoa já está na lista de sócios' })
      return
    }

    try {
      const person = await findPersonByDocument(document)
      const updatedPersons = [...persons]
      updatedPersons[createPersonIndex] = person
      setPersons(updatedPersons)
      if (createPersonIndex >= fieldPersons.length) addField()
      setShowCreatePersonModal(false)
      setEditingIndex(createPersonIndex)
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response?.data?.message ?? 'Erro ao carregar os dados da pessoa.'
      })
    }
  }

  async function submit() {
    await Swal.fire({
      title: 'Deseja atualizar os sócios?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary' // Classe personalizada para o botão de cancelamento (opcional)
      }
    }).then(async (result) => {
      if (result.isConfirmed && currentOwners.length > 0) {
        setLoading(true)
        try {
          const data = { owners: currentOwners.map((person) => person.id) }
          await updateOwners(formik.values.id, data)
          setSavedOwners(currentOwners)
          formik.setFieldValue('owners', data.owners)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Salvo com sucesso',
            showConfirmButton: false,
            timer: 1500
          })
        } catch (error: any) {
          if (error.response.status === 422) {
            let errorText = '';
            for (let key in error.response.data.errors) {
              errorText += '<li>' + error.response.data.errors[key][0] + '</li>';
            }
            await Swal.fire({
              icon: 'error',
              title: 'Ops! Revise seu formulário e tente novamente!',
              html: errorText
            });
          } else {
            await Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: error.response.data.message,
              timer: 2000
            })
          }
        } finally {
          setLoading(false)
        }
      }
    })
  }
  return (
    <>
      <div className='card mb-5 mb-xl-10'>
        <div className="card card-custom">
          <div className="card-header">
            <h3 className="card-title fw-bolder m-0">Sócios</h3>
            <div className="card-toolbar">
              <button type='reset' onClick={(e) => {
                e.preventDefault();
                setShowCreatePersonModal(true)
              }} className="btn btn-danger">
                Criar Sócio
              </button>
            </div>
          </div>
          <div className="card-body">
            <table className="table table-row-dashed table-row-gray-300 gy-7" key='Owner'>
              <tbody>
                {
                  fieldPersons.map((field, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          {persons?.[index] === undefined &&
                            <SearchPersonForm field={field} index={index} provider={usePeople} textTitle = {'Selecione o sócio'} />}
                          {persons?.[index] !== undefined &&
                            <PersonInfo person={persons[index]} responsible={false} provider={ownerProvider(index)} index={index} hasBudget={false} source="OWNER"
                              onSave={(req) => createOwner(formik.values.id, req)} />}
                        </td>
                        <td className="align-middle">
                          {index > 0 &&
                            <button
                              id={`${index}`}
                              onClick={(e) => {
                                removeField(index)
                              }}
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          }
                        </td>
                      </tr>
                    )
                  })
                }
              </tbody>
            </table>
            <button type='reset' onClick={(e) => {
              e.preventDefault();
              addField();
            }} className="btn btn-danger">
              Adicionar Sócios
            </button>
          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            {isDirty && (
              <button type="reset" className="btn btn-secondary me-2" disabled={loading} onClick={discardChanges}>
                Descartar alterações
              </button>
            )}
            <button type='submit' className='btn btn-danger' disabled={loading || !currentOwners.length || !isDirty}
              onClick={(e) => { e.preventDefault(); submit() }}>
              {!loading && 'Salvar'}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Salvando...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      {showCreatePersonModal && (
        <PersonCreateModal
          person={initialValues}
          index={createPersonIndex}
          onCreate={(req) => createOwner(formik.values.id, req)}
          onValidate={validateOwnerPerson}
          onExistingPerson={addExistingOwner}
          entityName='sócio'
          action={(person) => {
            setSavedOwners((owners) => [...owners, person])
            if (createPersonIndex >= fieldPersons.length) addField()
          }}
        />
      )}
    </>
  );

};